import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/useAuth.jsx';
import { useAdminAccess } from '../lib/useAdminAccess.jsx';
import './AdminLogin.css';

// AdminLogin handles both ends of the magic link flow:
//
//   1. First visit: shows the email entry form. On submit, sends a
//      sign-in link to the email and shows a "check your inbox"
//      confirmation.
//
//   2. Return visit (clicked the link in email): detects the magic
//      link parameters in the URL, completes sign-in, and routes
//      the user based on their access level.
//
// Routing after successful sign-in:
//   - Super-admin → /admin/communities
//   - Community admin with one community → that community's roster
//   - Community admin with multiple → /admin/picker
//   - Otherwise → /admin/no-access
//
// If a returnTo was provided in location state (e.g. they tried to
// access an admin page while logged out), super-admins go there;
// community admins still get routed to their roster (because their
// returnTo may be a page they can't access).
export default function AdminLogin() {
  const { user, sendLoginLink, tryCompleteLinkSignIn } = useAuth();
  const { isSuperAdmin, communities, loading: accessLoading } = useAdminAccess();
  const navigate = useNavigate();
  const location = useLocation();

  const [state, setState] = useState('idle');
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);

  const returnTo = location.state?.returnTo || null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await tryCompleteLinkSignIn();
      if (cancelled) return;
      if (result.handled) {
        if (result.error) {
          setState('error');
          setError(result.error);
        } else {
          setState('completing');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [tryCompleteLinkSignIn]);

  // Route the signed-in user based on their access. We wait for
  // accessLoading to be false so we know exactly what they have.
  useEffect(() => {
    if (!user || accessLoading) return;

    if (isSuperAdmin) {
      navigate(returnTo || '/admin/communities', { replace: true });
      return;
    }

    if (communities.length === 1) {
      navigate(
        `/admin/communities/${encodeURIComponent(communities[0].code)}/residents`,
        { replace: true }
      );
      return;
    }

    if (communities.length > 1) {
      navigate('/admin/picker', { replace: true });
      return;
    }

    // Signed in but no access. AdminNoAccess explains the situation.
    navigate('/admin/no-access', { replace: true });
  }, [user, isSuperAdmin, communities, accessLoading, navigate, returnTo]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setState('sending');
    try {
      await sendLoginLink(email);
      setState('sent');
    } catch (err) {
      setState('error');
      setError(err.message);
    }
  }

  // ----- Render -----

  if (state === 'completing') {
    return (
      <div className="container admin-login admin-login-narrow">
        <p className="admin-login-meta">Signing you in…</p>
      </div>
    );
  }

  if (state === 'sent') {
    return (
      <div className="container admin-login admin-login-narrow">
        <span className="eyebrow fade-in-1">Check Your Email</span>
        <h1 className="admin-login-title fade-in-2">
          A sign-in link is on <em>its way</em>.
        </h1>
        <div className="admin-login-body fade-in-3">
          <p>
            We sent a sign-in link to <strong>{email}</strong>. Open the
            email on this device and tap the link to complete sign-in.
          </p>
          <p className="admin-login-note">
            The link expires after a short period. If you don't see the
            email within a minute, check your spam folder, then{' '}
            <button
              type="button"
              className="admin-login-link"
              onClick={() => { setState('idle'); setError(null); }}
            >
              try again
            </button>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container admin-login admin-login-narrow">
      <span className="eyebrow fade-in-1">Administrator Sign In</span>
      <h1 className="admin-login-title fade-in-2">
        Welcome <em>back</em>.
      </h1>
      <div className="admin-login-body fade-in-3">
        <p>
          Sign in with the email address associated with your community.
          We'll send a one-time link to your inbox — no password
          required.
        </p>

        <form onSubmit={handleSubmit} className="admin-login-form">
          <label htmlFor="admin-email" className="admin-login-label">
            Email address
          </label>
          <input
            id="admin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yourcommunity.com"
            required
            autoComplete="email"
            autoFocus
            className="admin-login-input"
            disabled={state === 'sending'}
          />

          {error ? (
            <p className="admin-login-error" role="alert">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            className="btn"
            disabled={state === 'sending' || !email}
          >
            {state === 'sending' ? 'Sending link…' : 'Send sign-in link'}
          </button>
        </form>
      </div>
    </div>
  );
}
